import { cloneDeep, isEqual } from "lodash-es";
import { type Ref, ref, watch } from "vue";

interface ServerDraftOptions<S> {
  /** Runs each time the draft starts over, for state kept beside it: validation, derived inputs. */
  onReset?: (snapshot: S) => void;
  /** Runs `onReset` at setup too, for state it initialises rather than only clears. */
  immediate?: boolean;
}

/**
 * A form's editable copy of values the server owns.
 *
 * The draft starts over only when `server()` yields values unequal to the ones it last started from,
 * never merely because new objects arrived: the account store replaces the account's objects with
 * every answer it takes, a reload after a signed document included, and starting over then would
 * throw away what the user is typing. So `server()` projects just the form's fields, plus the record's
 * id so that another record with equal values still starts over; `toDraft` drops what the form
 * doesn't edit, such as that id.
 */
export function useServerDraft<S, D>(
  server: () => S,
  toDraft: (snapshot: S) => D,
  { onReset, immediate = false }: ServerDraftOptions<S> = {}
): Ref<D> {
  const initial = server();
  // Compared with a deep copy of its own: `toDraft` may hand over any object inside the snapshot,
  // and were they shared, the user's edits would read as a server change and be thrown away.
  let snapshot = cloneDeep(initial);
  const draft = ref(toDraft(initial)) as Ref<D>;
  if (immediate) onReset?.(initial);

  watch(server, (next) => {
    if (isEqual(next, snapshot)) return;
    snapshot = cloneDeep(next);
    draft.value = toDraft(next);
    onReset?.(next);
  });

  return draft;
}
